import { useState, useEffect } from "react";
import { Link, useParams, useLocation, useNavigate } from "react-router-dom";
import { Box, Breadcrumbs, MenuItem } from "@mui/material";
import Grid from "@mui/material/Grid";
import GridTable from "../../../components/GridTable";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import BusinessIcon from "@mui/icons-material/Business";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import TextField from "@mui/material/TextField";
import Dialog from "../../../components/Dialog";
import Loader from "../../../components/Loader";
import Button from "../../../components/Button";
import ActionMenu from "../../../components/ActionMenu";
import { successMessage, errorMessage } from "../../../components/Toaster";
import {
  getYardsByCompanyId,
  createYard,
  updateYard,
  deleteYard,
  getSiteTypes,
} from "./api";
import { getErrorMessage } from "../../../utils/error";
import { SUPER_ADMIN, VISO_SUPER_ADMIN } from "../../../constants";

const Yard = () => {
  const location = useLocation();
  const { company_id } = useParams();
  const navigate = useNavigate();
  const company_name =
    location.state?.company_name ||
    localStorage.getItem("company_name") ||
    "Company";

  // NEW — role drives whether breadcrumbs point at the global company list
  // or back to this company's own yards page.
  const role = localStorage.getItem("role_name");
  const isVisoSuperAdmin = role === VISO_SUPER_ADMIN;

  const [yards, setYards] = useState([]);
  const [siteTypes, setSiteTypes] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState({
    fetch: false,
    create: false,
    update: false,
    delete: false,
    siteTypes: false,
  });
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [editYard, setEditYard] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const validationSchema = Yup.object().shape({
    yardName: Yup.string().required("Yard Name is required"),
    siteType: Yup.string().required("Site Type is required"),
    address: Yup.string().required("Address is required"),
  });

  // NEW — where the {company_name} breadcrumb goes, based on role.
  // VISO_SUPER_ADMIN has a global company list to fall back to.
  // SUPER_ADMIN is scoped to one company, so it points back to their own yards page.
  const companyBreadcrumbPath = isVisoSuperAdmin
    ? "/company"
    : `/company/${company_id}/yards`;

  const columns = [
    {
      field: "yard_name",
      headerName: "Yard Name",
      headerClassName: "table-header",
      flex: 1,
    },
    {
      field: "site_type",
      headerName: "Site Type",
      headerClassName: "table-header",
      flex: 1,
    },
    {
      field: "address",
      headerName: "Address",
      headerClassName: "table-header",
      flex: 1,
    },
    {
      field: "actions",
      headerName: "Actions",
      headerClassName: "table-header",
      renderCell: (params) => (
        <ActionMenu
          params={params}
          actions={[
            {
              icon: <EditIcon color="primary" />,
              tooltip: "Edit",
              onClick: handleEdit,
            },
            {
              icon: <DeleteIcon color="error" />,
              tooltip: "Delete",
              onClick: handleDelete,
            },
            {
              icon: <AccountCircleIcon color="action" />,
              tooltip: "Users",
              onClick: handleViewUsers,
            },
            {
              icon: <Inventory2Icon color="success" />,
              tooltip: "Machines",
              onClick: handleViewMachines,
            },
          ]}
        />
      ),
      flex: 1,
    },
  ];

  useEffect(() => {
    const fetchYards = async () => {
      setLoading((p) => ({ ...p, fetch: true }));
      try {
        const res = await getYardsByCompanyId(company_id || "");
        if (res.status) {
          setYards(res.data);
        } else {
          errorMessage(res.message || "Failed to fetch yards");
        }
      } catch (err) {
        errorMessage(getErrorMessage(err) || "Error fetching yards");
      } finally {
        setLoading((p) => ({ ...p, fetch: false }));
      }
    };

    if (company_id) fetchYards();
  }, [company_id]);

  useEffect(() => {
    const fetchSiteTypes = async () => {
      setLoading((p) => ({ ...p, siteTypes: true }));
      try {
        const res = await getSiteTypes();
        if (res.status) {
          setSiteTypes(res.data || []);
        } else {
          errorMessage(res.message || "Failed to fetch site types");
        }
      } catch (err) {
        errorMessage(getErrorMessage(err) || "Error fetching site types");
      } finally {
        setLoading((p) => ({ ...p, siteTypes: false }));
      }
    };

    fetchSiteTypes();
  }, []);

  const openDialog = () => setDialogOpen(true);

  const closeDialog = (resetForm) => {
    resetForm();
    setDialogOpen(false);
    setEditYard(null);
  };

  const handleEdit = (params) => {
    setEditYard(params.row);
    openDialog();
  };

  const handleDelete = (params) => {
    setItemToDelete(params);
    setDeleteOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteOpen(false);
    setItemToDelete(null);
  };

  const handleViewUsers = (params) => {
    const yard_id = params.row.yard_id;
    const yard_name = params.row.yard_name;
    navigate(`/company/${company_id}/yards/${yard_id}/users`, {
      state: { company_name, yard_name },
    });
  };

  const handleViewMachines = (params) => {
    const yard_id = params.row.yard_id;
    const yard_name = params.row.yard_name;
    navigate(`/company/${company_id}/yards/${yard_id}/machines`, {
      state: { company_name, yard_name },
    });
  };

  const confirmDialog = async (values, { resetForm }) => {
    const success = editYard
      ? await handleUpdate(values)
      : await handleCreate(values);

    if (success) closeDialog(resetForm);
  };

  const handleCreate = async (values) => {
    setLoading((p) => ({ ...p, create: true }));
    try {
      const payload = {
        yard_name: values.yardName.trim(),
        company_id: company_id,
        site_type: values.siteType,
        address: values.address.trim(),
      };

      const res = await createYard(payload);
      if (res.status) {
        setYards((prev = []) => [
          ...(Array.isArray(prev) ? prev : []),
          { ...payload, yard_id: res.data.yard_id },
        ]);
        successMessage(res.message);
        return true;
      } else {
        errorMessage(res.message);
        return false;
      }
    } catch (err) {
      errorMessage(getErrorMessage(err));
      return false;
    } finally {
      setLoading((p) => ({ ...p, create: false }));
    }
  };

  const handleUpdate = async (values) => {
    setLoading((p) => ({ ...p, update: true }));
    try {
      const payload = {
        yard_id: editYard.yard_id,
        yard_name: values.yardName.trim(),
        company_id: company_id,
        site_type: values.siteType,
        address: values.address.trim(),
      };

      const res = await updateYard(payload);
      if (res.status) {
        setYards((prev) =>
          prev.map((y) =>
            y.yard_id === editYard.yard_id
              ? {
                  ...y,
                  yard_name: values.yardName,
                  site_type: values.siteType,
                  address: values.address,
                }
              : y,
          ),
        );
        successMessage(res.message);
        return true;
      } else {
        errorMessage(res.message);
        return false;
      }
    } catch (err) {
      errorMessage(getErrorMessage(err));
      return false;
    } finally {
      setLoading((p) => ({ ...p, update: false }));
    }
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    setLoading((p) => ({ ...p, delete: true }));
    try {
      const res = await deleteYard(itemToDelete.row.yard_id);
      if (res.status) {
        setYards((prev) =>
          prev.filter((y) => y.yard_id !== itemToDelete.row.yard_id),
        );
        successMessage(res.message);
      } else {
        errorMessage(res.message);
      }
    } catch (err) {
      errorMessage(getErrorMessage(err));
    } finally {
      setLoading((p) => ({ ...p, delete: false }));
      setDeleteOpen(false);
    }
  };

  return (
    <>
      {loading.fetch ? (
        <Loader color="primary" size={40} />
      ) : (
        <Box>
          <main className="m-a-50">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "30px",
              }}
            >
              <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small" />}
                aria-label="breadcrumb"
                style={{ fontSize: "18px", fontWeight: "bold" }}
              >
                {isVisoSuperAdmin ? (
                  <Link color="primary" to="/company">
                    Company
                  </Link>
                ) : (
                  <span style={{ color: "inherit", cursor: "default" }}>
                    Company
                  </span>
                )}
                <Link color="primary" to={companyBreadcrumbPath}>
                  {company_name}
                </Link>
                <Link
                  color="primary"
                  to={`/company/${company_id}/yards`}
                  state={{ company_name: company_name }}
                >
                  Yards
                </Link>
              </Breadcrumbs>
            </div>
            <div className="d-flex justify-end">
              <div className="d-flex justify-between w-100">
                <TextField
                  className="w-25"
                  label="Search"
                  variant="outlined"
                  value={searchQuery}
                  size="small"
                  startIcon={<SearchIcon />}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <Button
                  label="Add"
                  variant="contained"
                  color="primary"
                  onClick={openDialog}
                />
              </div>
            </div>
            <div
              style={{ height: "100%", width: "100%" }}
              className="ag-theme-alpine m-t-30"
            >
              <GridTable
                rows={yards}
                columns={columns}
                pageSize={10}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                getRowId={(row) => row.yard_id}
                density="standard"
              />
            </div>
          </main>
        </Box>
      )}
      <Formik
        initialValues={{
          yardName: editYard ? editYard.yard_name : "",
          siteType: editYard ? editYard.site_type : "",
          address: editYard ? editYard.address : "",
        }}
        validationSchema={validationSchema}
        onSubmit={confirmDialog}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleSubmit,
          resetForm,
        }) => (
          <>
            <Dialog
              open={isDialogOpen}
              onClose={() => closeDialog(resetForm)}
              onConfirm={handleSubmit}
              title={
                !loading.update && !loading.create
                  ? editYard
                    ? "Edit Yard"
                    : "Add New Yard"
                  : ""
              }
              confirmButtonText={
                !loading.update && !loading.create
                  ? editYard
                    ? "Update"
                    : "Add"
                  : ""
              }
              cancelButtonText={
                !loading.update && !loading.create ? "Cancel" : ""
              }
              icon={<BusinessIcon />}
              message={
                <Box padding={4}>
                  {loading.update || loading.create ? (
                    <Loader color="primary" size={40} />
                  ) : (
                    <Form>
                      <Grid container spacing={2}>
                        <Grid size={6}>
                          <TextField
                            id="yardName"
                            name="yardName"
                            label="Yard Name"
                            required
                            fullWidth
                            value={values.yardName}
                            onChange={handleChange}
                            error={touched.yardName && Boolean(errors.yardName)}
                            helperText={touched.yardName && errors.yardName}
                          />
                        </Grid>
                        <Grid size={6}>
                          <TextField
                            id="siteType"
                            name="siteType"
                            label="Site Type"
                            select
                            required
                            fullWidth
                            value={values.siteType}
                            onChange={handleChange}
                            error={touched.siteType && Boolean(errors.siteType)}
                            helperText={touched.siteType && errors.siteType}
                            disabled={loading.siteTypes}
                          >
                            {siteTypes.map((type) => (
                              <MenuItem key={type} value={type}>
                                {type}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid size={6}>
                          <TextField
                            id="address"
                            name="address"
                            label="Address"
                            required
                            fullWidth
                            multiline
                            rows={2}
                            value={values.address}
                            onChange={handleChange}
                            error={touched.address && Boolean(errors.address)}
                            helperText={touched.address && errors.address}
                          />
                        </Grid>
                      </Grid>
                    </Form>
                  )}
                </Box>
              }
            />
          </>
        )}
      </Formik>
      <Dialog
        open={deleteOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={!loading.delete ? "Delete Yard" : ""}
        confirmButtonText={!loading.delete ? "Confirm" : ""}
        cancelButtonText={!loading.delete ? "Cancel" : ""}
        icon={<DeleteIcon />}
        message={
          <Box padding={2}>
            {loading.delete ? (
              <Loader color="primary" size={40} />
            ) : (
              "Are you sure you want to delete this yard?"
            )}
          </Box>
        }
      />
    </>
  );
};

export default Yard;
